/**
 * Google Sign In Controller
 *
 * Handles the Google Sign In flow using Google Identity Services (GIS).
 * When clicked, prompts the user to sign in with their Google account,
 * then submits the ID token to the backend for validation.
 *
 * Usage:
 * <div data-controller="google-signin"
 *      data-google-signin-client-id-value="your-google-client-id"
 *      data-google-signin-callback-url-value="/auth/social/callback/">
 *   <button data-action="click->google-signin#signIn">
 *     Continue with Google
 *   </button>
 * </div>
 */

import ApplicationController from './application_controller';

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: GoogleInitConfig) => void;
          prompt: (callback?: (notification: PromptNotification) => void) => void;
          renderButton: (element: HTMLElement, config: ButtonConfig) => void;
        };
      };
    };
  }
}

interface GoogleInitConfig {
  client_id: string;
  callback: (response: GoogleCredentialResponse) => void;
  auto_select?: boolean;
  cancel_on_tap_outside?: boolean;
}

interface GoogleCredentialResponse {
  credential: string;
  select_by: string;
}

interface PromptNotification {
  isNotDisplayed: () => boolean;
  isSkippedMoment: () => boolean;
  isDismissedMoment: () => boolean;
  getNotDisplayedReason: () => string;
  getSkippedReason: () => string;
  getDismissedReason: () => string;
}

interface ButtonConfig {
  type?: 'standard' | 'icon';
  theme?: 'outline' | 'filled_blue' | 'filled_black';
  size?: 'large' | 'medium' | 'small';
  text?: 'signin_with' | 'signup_with' | 'continue_with' | 'signin';
  shape?: 'rectangular' | 'pill' | 'circle' | 'square';
  logo_alignment?: 'left' | 'center';
  width?: string;
  locale?: string;
}

export default class extends ApplicationController {
  static values = {
    clientId: String,
    callbackUrl: { type: String, default: '/auth/social/callback/' },
    nextUrl: { type: String, default: '' },
  };

  declare clientIdValue: string;
  declare callbackUrlValue: string;
  declare nextUrlValue: string;

  private sdkLoaded: boolean = false;
  private sdkLoading: boolean = false;

  connect(): void {
    if (!this.clientIdValue) {
      console.warn('Google Sign In: Missing client ID');
      return;
    }
    this.loadGoogleSDK();
  }

  /**
   * Load the Google Identity Services SDK
   */
  private loadGoogleSDK(): void {
    if (this.sdkLoaded || this.sdkLoading) return;

    // Check if SDK is already loaded
    if (window.google?.accounts?.id) {
      this.sdkLoaded = true;
      this.initializeGoogle();
      return;
    }

    this.sdkLoading = true;

    const script = document.createElement('script');
    script.src = 'https://accounts.google.com/gsi/client';
    script.async = true;
    script.defer = true;
    script.onload = () => {
      this.sdkLoaded = true;
      this.sdkLoading = false;
      this.initializeGoogle();
    };
    script.onerror = () => {
      this.sdkLoading = false;
      console.error('Google Sign In: Failed to load SDK');
    };

    document.head.appendChild(script);
  }

  /**
   * Initialize Google Identity Services
   */
  private initializeGoogle(): void {
    if (!window.google?.accounts?.id) {
      console.error('Google Sign In: SDK not available');
      return;
    }

    window.google.accounts.id.initialize({
      client_id: this.clientIdValue,
      callback: this.handleCredentialResponse.bind(this),
      auto_select: false,
      cancel_on_tap_outside: true,
    });
  }

  /**
   * Trigger Google Sign In flow
   */
  signIn(event?: Event): void {
    if (event) event.preventDefault();

    if (!window.google?.accounts?.id) {
      console.error('Google Sign In: SDK not ready');
      return;
    }

    // Show Google One Tap or redirect to Google sign-in
    window.google.accounts.id.prompt((notification) => {
      if (notification.isNotDisplayed()) {
        console.log('Google prompt not displayed:', notification.getNotDisplayedReason());
        // Could fall back to redirect flow here if needed
      } else if (notification.isSkippedMoment()) {
        console.log('Google prompt skipped:', notification.getSkippedReason());
      } else if (notification.isDismissedMoment()) {
        console.log('Google prompt dismissed:', notification.getDismissedReason());
      }
    });
  }

  /**
   * Handle the credential response from Google
   */
  private handleCredentialResponse(response: GoogleCredentialResponse): void {
    if (!response.credential) {
      console.error('Google Sign In: No credential received');
      return;
    }

    // Submit the ID token to our backend
    this.submitToBackend(response.credential);
  }

  /**
   * Submit the ID token to the backend for validation
   */
  private submitToBackend(idToken: string): void {
    // Create a form and submit it
    const form = document.createElement('form');
    form.method = 'POST';
    form.action = this.callbackUrlValue;
    form.style.display = 'none';

    // Add CSRF token
    const csrfToken = this.getCSRFToken();
    if (csrfToken) {
      const csrfInput = document.createElement('input');
      csrfInput.type = 'hidden';
      csrfInput.name = 'csrfmiddlewaretoken';
      csrfInput.value = csrfToken;
      form.appendChild(csrfInput);
    }

    // Add provider
    const providerInput = document.createElement('input');
    providerInput.type = 'hidden';
    providerInput.name = 'provider';
    providerInput.value = 'google';
    form.appendChild(providerInput);

    // Add ID token
    const tokenInput = document.createElement('input');
    tokenInput.type = 'hidden';
    tokenInput.name = 'id_token';
    tokenInput.value = idToken;
    form.appendChild(tokenInput);

    // Add next URL if present
    if (this.nextUrlValue) {
      const nextInput = document.createElement('input');
      nextInput.type = 'hidden';
      nextInput.name = 'next';
      nextInput.value = this.nextUrlValue;
      form.appendChild(nextInput);
    }

    document.body.appendChild(form);
    form.submit();
  }

  /**
   * Get CSRF token from cookies or meta tag
   */
  private getCSRFToken(): string | null {
    // Try meta tag first
    const metaTag = document.querySelector('meta[name="csrf-token"]');
    if (metaTag) {
      return metaTag.getAttribute('content');
    }

    // Fall back to cookie
    const cookies = document.cookie.split(';');
    for (const cookie of cookies) {
      const [name, value] = cookie.trim().split('=');
      if (name === 'csrftoken') {
        return value;
      }
    }

    return null;
  }
}
